import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import DashboardLayout from "./components/DashboardLayout";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AuthCallback from "./pages/AuthCallback";
import DashboardPage from "./pages/DashboardPage";
import PortfolioRedesignPage from "./pages/PortfolioRedesignPage";
import FXEnginePage from "./pages/FXEnginePage";
import DealAnalyzerPage from "./pages/DealAnalyzerPage";
import ContractRadarPage from "./pages/ContractRadarPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import DocumentVaultPage from "./pages/DocumentVaultPage";
import MobilityTrackerPage from "./pages/MobilityTrackerPage";
import RegulatoryPage from "./pages/RegulatoryPage";
import NotificationsPage from "./pages/NotificationsPage";
import SupportPage from "./pages/SupportPage";
import SettingsPage from "./pages/SettingsPage";
import LegalNoticePage from "./pages/LegalNoticePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import LandingPage from "./pages/LandingPage";
import FeedbackPage from "./pages/FeedbackPage";

import { CurrencyProvider } from "./contexts/CurrencyContext";
import { Toaster } from "./components/ui/sonner";
import { ConfirmProvider } from "./components/ConfirmDialog";
import LegalGatekeeperModal from "./components/LegalGatekeeperModal";

function ProtectedRoute({ children, requireAdmin = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/30 text-sm font-sans">Loading EQUIA...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Legacy dashboard is admin-only. Regular authenticated users are redirected
  // to the default /portfolio home; unauthenticated users are sent to /login
  // above. The legacy UI is never reachable by URL for non-admins.
  if (requireAdmin && user.role !== "admin") return <Navigate to="/portfolio" replace />;

  return (
    <DashboardLayout>
      {children}
      <LegalGatekeeperModal />
    </DashboardLayout>
  );
}

function AppRoutes() {
  const location = useLocation();

  // CRITICAL: Detect session_id in URL fragment synchronously during render
  // This prevents race conditions with ProtectedRoute's auth check
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/legal" element={<LegalNoticePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/dashboard" element={<ProtectedRoute requireAdmin><DashboardPage /></ProtectedRoute>} />
      <Route path="/portfolio" element={<ProtectedRoute><PortfolioRedesignPage /></ProtectedRoute>} />
      <Route path="/fx" element={<ProtectedRoute><FXEnginePage /></ProtectedRoute>} />
      <Route path="/deals" element={<ProtectedRoute><DealAnalyzerPage /></ProtectedRoute>} />
      <Route path="/contract-radar" element={<ProtectedRoute><ContractRadarPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><DocumentVaultPage /></ProtectedRoute>} />
      <Route path="/mobility" element={<ProtectedRoute><MobilityTrackerPage /></ProtectedRoute>} />
      <Route path="/news" element={<ProtectedRoute><RegulatoryPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CurrencyProvider>
          <ConfirmProvider>
            <AppRoutes />
            <Toaster />
          </ConfirmProvider>
        </CurrencyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
